import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack
} from "@chakra-ui/react";

import { Member, useAppStore } from "../store";
import { useDebugStore } from "../store/debugStore";

interface DebugStoreDisplayProps {
  fullWidth?: boolean;
  noMaxHeight?: boolean;
  position?: "top" | "default";
}

/**
 * Debug component that displays all variables in the global store
 * Only visible when debug mode is active
 */
const DebugStoreDisplay = ({
  fullWidth = false,
  noMaxHeight = false,
  position = "default"
}: DebugStoreDisplayProps) => {
  // Get the entire store state
  const store = useAppStore();
  const debugMode = store.debugMode;
  const currentMember = store.currentMember;
  const currentGroupMember = store.currentGroupMember;
  const userHomeData = store.userHomeData;

  // Get render tally from debug store
  const getSortedRenderTally = useDebugStore((state) => state.getSortedRenderTally);
  const resetRenderTally = useDebugStore((state) => state.resetRenderTally);
  const resetKey = useDebugStore((state) => state.resetKey);
  const openAccordionIndexes = useDebugStore((state) => state.openAccordionIndexes);
  const setOpenAccordionIndexes = useDebugStore((state) => state.setOpenAccordionIndexes);

  // Handle accordion state change
  const handleAccordionChange = (expandedIndexes: number[]) => {
    setOpenAccordionIndexes(expandedIndexes);
  };

  // Fixed colors for debug display
  const bgColor = "green.300"; // Bright green background
  const boxBgColor = "green.100";
  const textColor = "gray.800"; // Dark text
  const mutedTextColor = "gray.600";
  const borderColor = "green.500";
  const codeBgColor = "green.50";

  // Handler for resetting render tally
  const handleResetRenderTally = () => {
    resetRenderTally();
  };

  // Don't render anything if debug mode is not active
  if (!debugMode) {
    return null;
  }

  // Format member data specifically
  const formatMember = (member: Member | undefined) => {
    if (!member) return null;

    return (
      <Box>
        <Text fontWeight="bold" color={textColor}>
          id: <Badge colorScheme="purple">{member.id}</Badge>
        </Text>
        <Text color={textColor}>name: {member.name}</Text>
        <Text color={textColor}>birthdate: {member.birthdate || "N/A"}</Text>
        <Text color={textColor}>
          gender:{" "}
          <Badge colorScheme={member.gender === "M" ? "blue" : member.gender === "F" ? "pink" : "purple"}>
            {member.gender === "M" ? "Male" : member.gender === "F" ? "Female" : "Other"}
          </Badge>
        </Text>
        {member.email && <Text color={textColor}>email: {member.email}</Text>}
        {member.age && <Text color={textColor}>age: {member.age}</Text>}
      </Box>
    );
  };

  // Create a cleaner representation of the store for display
  // Exclude functions from the displayed data
  const displayableStore = {
    userInfo: store.userInfo,
    isAuthorized: store.isAuthorized,
    filter: store.filter,
    currentMember: store.currentMember,
    currentGroupMember: store.currentGroupMember,
    editMode: store.editMode,
    debugMode: store.debugMode,
    memberEntryMode: store.memberEntryMode
  };

  // Render table with component render counts
  const renderTallyTable = () => {
    // Get render counts again to ensure fresh data
    const sortedTally = getSortedRenderTally();

    return (
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th color={textColor}>Component</Th>
            <Th color={textColor} isNumeric>
              Renders
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedTally.map(([componentName, count], index) => (
            <Tr key={`${index}-${componentName}-${resetKey}`}>
              <Td color={textColor}>{componentName}</Td>
              <Td color={textColor} isNumeric>
                <Badge colorScheme="purple">{count}</Badge>
              </Td>
            </Tr>
          ))}
          {sortedTally.length === 0 && (
            <Tr>
              <Td colSpan={2} color={mutedTextColor} textAlign="center">
                No components have been rendered yet
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    );
  };

  // Custom accordion item that makes the whole header clickable but handles buttons correctly
  const CustomAccordionItem = (props: {
    index: number;
    title: React.ReactNode;
    children: React.ReactNode;
    borderColor: string;
  }) => {
    const { index, title, children, borderColor } = props;

    return (
      <AccordionItem borderColor={borderColor}>
        <h2>
          <AccordionButton _hover={{ bg: "green.400" }} padding={0}>
            <Box
              flex="1"
              textAlign="left"
              fontWeight="bold"
              color={textColor}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              padding="8px"
              cursor="pointer"
            >
              {title}
              <AccordionIcon color={textColor} />
            </Box>
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4} bg={boxBgColor}>
          {children}
        </AccordionPanel>
      </AccordionItem>
    );
  };

  return (
    <Box
      backgroundColor={bgColor}
      borderRadius={position === "top" ? "0" : "md"}
      padding={4}
      marginBottom={4}
      boxShadow={position === "top" ? "none" : "sm"}
      width={fullWidth ? "100%" : "100%"}
      overflow="auto"
      maxHeight={noMaxHeight ? "none" : "400px"}
      borderWidth={position === "top" ? "0 0 1px 0" : "1px"}
      borderColor={borderColor}
    >
      <Heading as="h3" size="md" mb={2} color={textColor}>
        Debug: Global Store State {resetKey > 0 && `(Reset: ${resetKey})`}
      </Heading>
      <Divider mb={3} borderColor={borderColor} />

      <Accordion allowMultiple index={openAccordionIndexes} onChange={handleAccordionChange}>
        {/* Render Tally */}
        <CustomAccordionItem
          index={0}
          title={
            <Box flex="1" textAlign="left" fontWeight="bold" color={textColor} display="flex" alignItems="center">
              <Text flex="1">Component Render Tally</Text>
              <Button
                size="xs"
                colorScheme="red"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent accordion toggle
                  e.preventDefault(); // Prevent default behavior
                  handleResetRenderTally();
                }}
                mr={3}
                ml={2}
                _hover={{ bg: "red.500" }}
              >
                Reset Tally
              </Button>
            </Box>
          }
          borderColor={borderColor}
        >
          <VStack align="stretch" spacing={4}>
            {renderTallyTable()}
          </VStack>
        </CustomAccordionItem>

        {/* User Home Data */}
        <CustomAccordionItem
          index={1}
          title={
            <Box flex="1" textAlign="left" fontWeight="bold" color={textColor}>
              userHomeData
              {userHomeData && (
                <Badge ml={2} colorScheme="green">
                  Available
                </Badge>
              )}
            </Box>
          }
          borderColor={borderColor}
        >
          {userHomeData ? (
            <Box>
              <Heading as="h4" size="sm" mb={2} color={textColor}>
                User
              </Heading>
              <Code
                display="block"
                whiteSpace="pre"
                p={2}
                mb={4}
                borderRadius="sm"
                fontSize="sm"
                bg={codeBgColor}
                color={textColor}
                overflowX="auto"
              >
                {JSON.stringify(userHomeData.user, null, 2)}
              </Code>

              <Heading as="h4" size="sm" mb={2} color={textColor}>
                Default Group
              </Heading>
              {userHomeData.defaultGroup ? (
                <Code
                  display="block"
                  whiteSpace="pre"
                  p={2}
                  mb={4}
                  borderRadius="sm"
                  fontSize="sm"
                  bg={codeBgColor}
                  color={textColor}
                  overflowX="auto"
                >
                  {JSON.stringify(userHomeData.defaultGroup, null, 2)}
                </Code>
              ) : (
                <Text color={mutedTextColor} mb={4}>
                  No default group set
                </Text>
              )}

              <Heading as="h4" size="sm" mb={2} color={textColor}>
                Group Members
              </Heading>
              {userHomeData.groupMembers && userHomeData.groupMembers.length > 0 ? (
                <Code
                  display="block"
                  whiteSpace="pre"
                  p={2}
                  mb={4}
                  borderRadius="sm"
                  fontSize="sm"
                  bg={codeBgColor}
                  color={textColor}
                  overflowX="auto"
                >
                  {JSON.stringify(userHomeData.groupMembers, null, 2)}
                </Code>
              ) : (
                <Text color={mutedTextColor} mb={4}>
                  No group members available
                </Text>
              )}
            </Box>
          ) : (
            <Text color={mutedTextColor}>No home data available</Text>
          )}
        </CustomAccordionItem>

        {/* Current Member */}
        <CustomAccordionItem
          index={2}
          title={
            <Box flex="1" textAlign="left" fontWeight="bold" color={textColor}>
              currentMember
              {store.currentMember && (
                <Badge ml={2} colorScheme="green">
                  Active
                </Badge>
              )}
            </Box>
          }
          borderColor={borderColor}
        >
          {store.currentMember ? (
            formatMember(store.currentMember)
          ) : (
            <Text color={mutedTextColor}>No member selected</Text>
          )}
        </CustomAccordionItem>

        {/* Current Group Member */}
        <CustomAccordionItem
          index={3}
          title={
            <Box flex="1" textAlign="left" fontWeight="bold" color={textColor}>
              currentGroupMember
              {store.currentGroupMember && (
                <Badge ml={2} colorScheme="green">
                  Active
                </Badge>
              )}
            </Box>
          }
          borderColor={borderColor}
        >
          {store.currentGroupMember ? (
            <Code
              display="block"
              whiteSpace="pre"
              p={2}
              borderRadius="sm"
              fontSize="sm"
              bg={codeBgColor}
              color={textColor}
              overflowX="auto"
            >
              {JSON.stringify(store.currentGroupMember, null, 2)}
            </Code>
          ) : (
            <Text color={mutedTextColor}>No group member selected</Text>
          )}
        </CustomAccordionItem>

        {/* User Info */}
        <CustomAccordionItem
          index={4}
          title={
            <Box flex="1" textAlign="left" fontWeight="bold" color={textColor}>
              userInfo
              {store.userInfo && (
                <Badge ml={2} colorScheme="green">
                  Logged In
                </Badge>
              )}
            </Box>
          }
          borderColor={borderColor}
        >
          {store.userInfo ? (
            <Code
              display="block"
              whiteSpace="pre"
              p={2}
              borderRadius="sm"
              fontSize="sm"
              bg={codeBgColor}
              color={textColor}
            >
              {JSON.stringify(store.userInfo, null, 2)}
            </Code>
          ) : (
            <Text color={mutedTextColor}>Not logged in</Text>
          )}
        </CustomAccordionItem>

        {/* Other State */}
        <CustomAccordionItem
          index={5}
          title={
            <Box flex="1" textAlign="left" fontWeight="bold" color={textColor}>
              Other State
            </Box>
          }
          borderColor={borderColor}
        >
          <VStack align="stretch" spacing={2}>
            <Box p={2} bg="white" borderRadius="sm" borderWidth="1px" borderColor={borderColor}>
              <Text fontWeight="bold" color={textColor}>
                isAuthorized:
              </Text>
              <Badge colorScheme={store.isAuthorized ? "green" : "red"}>{store.isAuthorized ? "True" : "False"}</Badge>
            </Box>

            <Box p={2} bg="white" borderRadius="sm" borderWidth="1px" borderColor={borderColor}>
              <Text fontWeight="bold" color={textColor}>
                editMode:
              </Text>
              <Badge colorScheme={store.editMode ? "blue" : "gray"}>{store.editMode ? "Edit Mode" : "View Mode"}</Badge>
            </Box>

            <Box p={2} bg="white" borderRadius="sm" borderWidth="1px" borderColor={borderColor}>
              <Text fontWeight="bold" color={textColor}>
                filter:
              </Text>
              {store.filter ? (
                <Text color={textColor}>{store.filter}</Text>
              ) : (
                <Text color={mutedTextColor}>No filter</Text>
              )}
            </Box>

            <Box p={2} bg="white" borderRadius="sm" borderWidth="1px" borderColor={borderColor}>
              <Text fontWeight="bold" color={textColor}>
                debugMode:
              </Text>
              <Badge colorScheme="green">Active</Badge>
            </Box>

            <Box p={2} bg="white" borderRadius="sm" borderWidth="1px" borderColor={borderColor}>
              <Text fontWeight="bold" color={textColor}>
                memberEntryMode:
              </Text>
              <Badge colorScheme="blue">{store.memberEntryMode}</Badge>
            </Box>
          </VStack>
        </CustomAccordionItem>

        {/* Full Store (Raw) */}
        <CustomAccordionItem
          index={6}
          title={
            <Box flex="1" textAlign="left" fontWeight="bold" color={textColor}>
              Full Store (Raw)
            </Box>
          }
          borderColor={borderColor}
        >
          <Code
            display="block"
            whiteSpace="pre"
            p={2}
            borderRadius="sm"
            fontSize="sm"
            bg={codeBgColor}
            color={textColor}
            overflowX="auto"
          >
            {JSON.stringify(displayableStore, null, 2)}
          </Code>
        </CustomAccordionItem>
      </Accordion>
    </Box>
  );
};

export default DebugStoreDisplay;
